/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business バッジコンポーネント。5種のバリアント（default/success/warning/error/info）と2段階サイズをCSS変数で表現
 * @graph-connects none
 */
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../../lib/cn';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** バッジのバリアント */
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  /** バッジのサイズ */
  size?: 'sm' | 'md';
  /**
   * 背景を不透明（solid）にする。dark mode で muted 背景（rgba 透過）が背後を透かして
   * 見えてしまうのを避けたい status ラベル等に使う。light mode は muted と同値で変化なし。
   */
  solid?: boolean;
}

/** @graph-connects none */
const variantStyles = {
  default:
    'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border border-[var(--color-border)]',
  success:
    'bg-[var(--color-success-muted)] text-[var(--color-success)] border border-[var(--color-success)]/20',
  warning:
    'bg-[var(--color-warning-muted)] text-[var(--color-warning)] border border-[var(--color-warning)]/20',
  error:
    'bg-[var(--color-error-muted)] text-[var(--color-error)] border border-[var(--color-error)]/20',
  info: 'bg-[var(--color-info-muted)] text-[var(--color-info)] border border-[var(--color-info)]/20',
};

/** solid（不透明）背景バリアント。dark mode で透けない status バッジ用。 */
/** @graph-connects none */
const solidVariantStyles = {
  default:
    'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border border-[var(--color-border)]',
  success:
    'bg-[var(--color-success-solid)] text-[var(--color-success)] border border-[var(--color-success)]/20',
  warning:
    'bg-[var(--color-warning-solid)] text-[var(--color-warning)] border border-[var(--color-warning)]/20',
  error:
    'bg-[var(--color-error-solid)] text-[var(--color-error)] border border-[var(--color-error)]/20',
  info: 'bg-[var(--color-info-solid)] text-[var(--color-info)] border border-[var(--color-info)]/20',
};

/** @graph-connects none */
const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

/** @graph-connects none */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'default', size = 'sm', solid = false, className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center font-medium rounded-full',
          (solid ? solidVariantStyles : variantStyles)[variant],
          sizeStyles[size],
          className,
        )}
        {...props}
      >
        {children}
      </span>
    );
  },
);

Badge.displayName = 'Badge';
